import type { Candle, ChartInterval, MarketKind } from '../types/market';
import { getBinanceCandles } from './exchanges/binance';
import { getBybitCandles } from './exchanges/bybit';
import { getOkxCandles } from './exchanges/okx';

export interface CandleHistoryResult {
  candles: Candle[];
  source: string;
  marketKind: Exclude<MarketKind, 'dex'>;
  exchange: 'binance' | 'bybit' | 'okx';
}

type CandleExchange = CandleHistoryResult['exchange'];

interface CandleAttempt {
  source: string;
  exchange: CandleExchange;
  marketKind: 'spot' | 'futures';
  load: () => Promise<Candle[]>;
}

const MAX_HISTORY_REQUESTS = 6;
const QUEUE_WAIT_TIMEOUT_MS = 12_000;
let activeHistoryRequests = 0;
const historyQueue: Array<() => void> = [];

async function withHistorySlot<T>(task: () => Promise<T>): Promise<T> {
  if (activeHistoryRequests >= MAX_HISTORY_REQUESTS) {
    let queued = true;
    const dequeue = (): Promise<void> =>
      new Promise<void>((resolve) => {
        const entry = (): void => {
          if (queued) {
            queued = false;
            resolve();
          }
        };
        historyQueue.push(entry);
        setTimeout(() => {
          if (queued) {
            queued = false;
            const index = historyQueue.indexOf(entry);
            if (index !== -1) historyQueue.splice(index, 1);
            resolve();
          }
        }, QUEUE_WAIT_TIMEOUT_MS);
      });
    await dequeue();
  }
  activeHistoryRequests += 1;
  try {
    return await task();
  } finally {
    activeHistoryRequests -= 1;
    historyQueue.shift()?.();
  }
}

function pause(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

/**
 * Starts the preferred provider first and hedges to alternatives shortly after.
 * A shared request budget prevents a shortlist refresh from flooding public REST
 * endpoints. Queued fallbacks are skipped once one verified history wins.
 */
async function firstUsable(attempts: CandleAttempt[]): Promise<CandleHistoryResult> {
  return new Promise((resolve, reject) => {
    const errors: string[] = [];
    let remaining = attempts.length;
    let settled = false;

    const fail = (message: string): void => {
      if (settled) return;
      errors.push(message);
      remaining -= 1;
      if (remaining === 0) reject(new Error(errors.join(' | ')));
    };

    attempts.forEach((attempt, index) => {
      void (async () => {
        if (index > 0) await pause(index * 700);
        if (settled) return;
        try {
          const candles = await withHistorySlot(async () => {
            if (settled) return [];
            return attempt.load();
          });
          if (settled) return;
          if (candles.length === 0) {
            fail(`${attempt.source} returned no candles`);
            return;
          }
          settled = true;
          resolve({ candles, source: attempt.source, marketKind: attempt.marketKind, exchange: attempt.exchange });
        } catch (reason) {
          fail(`${attempt.source}: ${reason instanceof Error ? reason.message : 'request failed'}`);
        }
      })();
    });
  });
}

function attemptsForMarket(
  symbol: string,
  interval: ChartInterval,
  limit: number,
  marketKind: 'spot' | 'futures',
  serverNow: number,
  preferredExchange?: CandleExchange,
): CandleAttempt[] {
  const attempts: CandleAttempt[] = [
    {
      source: `binance:${marketKind}:candles`,
      exchange: 'binance',
      marketKind,
      load: () => getBinanceCandles(symbol, interval, limit, marketKind, serverNow),
    },
    {
      source: `bybit:${marketKind}:candles`,
      exchange: 'bybit',
      marketKind,
      load: () => getBybitCandles(symbol, interval, limit, marketKind, serverNow),
    },
    {
      source: `okx:${marketKind}:candles`,
      exchange: 'okx',
      marketKind,
      load: () => getOkxCandles(symbol, interval, limit, marketKind, serverNow),
    },
  ];
  if (!preferredExchange) return attempts;
  return attempts.sort((left, right) => Number(right.exchange === preferredExchange) - Number(left.exchange === preferredExchange));
}

export async function getVerifiedCandleHistory(
  symbol: string,
  interval: ChartInterval,
  limit: number,
  preferredMarket: 'spot' | 'futures',
  serverNow = Date.now(),
  preferredExchange?: CandleExchange,
): Promise<CandleHistoryResult> {
  const preferredAttempts = attemptsForMarket(symbol, interval, limit, preferredMarket, serverNow, preferredExchange);
  try {
    return await firstUsable(preferredAttempts);
  } catch (preferredError) {
    const alternate = preferredMarket === 'spot' ? 'futures' : 'spot';
    try {
      return await firstUsable(attemptsForMarket(symbol, interval, limit, alternate, serverNow, preferredExchange));
    } catch (alternateError) {
      const preferredMessage = preferredError instanceof Error ? preferredError.message : 'preferred market failed';
      const alternateMessage = alternateError instanceof Error ? alternateError.message : 'alternate market failed';
      throw new Error(`${preferredMessage} | ${alternateMessage}`);
    }
  }
}
